import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Modal } from 'antd';
import { toast, Bounce } from 'react-toastify';

/**
 * The "still saving" notice is the one toast in this file whose correct
 * lifetime is not a fixed number of seconds — it describes a request that is
 * still in flight, so it must live exactly as long as that request does.
 * `autoClose: false` hands that decision to `useUnsavedChangesGuard`, which
 * dismisses it the moment `isBusy` clears (or the form unmounts).
 */
const busyToastOpts = { position: 'top-right', autoClose: false, transition: Bounce };

/** Per-guard toast id, so repeated clicks re-use one notice instead of stacking
 *  a new one each time (react-toastify treats a repeated `toastId` as a no-op),
 *  while two different forms could still each own theirs. */
let busyToastSeq = 0;
const nextBusyToastId = () => `unsaved-changes-busy-${(busyToastSeq += 1)}`;

/**
 * Unsaved-changes gate for the full-width form slide-overs.
 *
 * Background: `EssFullWidthModal` is a slide-over positioned below the fixed
 * header and deliberately built to sit *beside* the sidebar rather than over
 * it. The width it gives back to the sidebar is measured, not assumed:
 * `baseModal` reserves `getBoundingClientRect().right` and only above 991px
 * (`baseModal.jsx:48,66`). Below that the sidebar is an off-canvas drawer
 * parked at `margin-left: -225px`, so nothing is reserved and the panel takes
 * the full width — an earlier `offsetWidth`-based calculation reserved 225px
 * there for a sidebar that was not on screen.
 *
 * antd still renders a full-viewport `.ant-modal-wrap` at `zIndexPopupBase`
 * (1100), which is above the sidebar at either of its z-indexes — 1001 on
 * desktop, 1041 once it becomes the drawer (`_sidebar.scss:14`) — and that
 * wrap is what swallows sidebar clicks. `baseModal` insets the wrap so the
 * sidebar column stays reachable, in the same >991px range where it reserves
 * the width; this context supplies the other half of the decision — leaving a
 * slide-over that has unsaved work must ask first.
 *
 * Three ways out of a slide-over route through here, so they all behave the
 * same: a sidebar click, the panel's own X button, and Esc.
 *
 * Only slide-overs that opt in (via `useUnsavedChangesGuard`) register here.
 * When nothing is registered the sidebar behaves exactly as it always has: the
 * click handler bails out before it touches the event.
 */
const UnsavedChangesContext = createContext(null);

/** Used when a consumer renders outside the provider (e.g. an isolated test
 *  harness). Reports "nothing to guard", so navigation is never blocked by the
 *  absence of the provider. */
const NO_GUARD = {
  registerGuard: () => () => {},
  hasGuard: () => false,
  confirmLeave: () => Promise.resolve(true),
  confirmLeaveFor: () => Promise.resolve(true),
};

export const UnsavedChangesProvider = ({ children }) => {
  // Hook form, not static Modal.confirm: the static call renders outside the
  // React tree and so never sees the app's ConfigProvider theme (index.jsx),
  // which is what left the module's old confirms unbranded and top-aligned.
  // The module has zero static confirms left and this must not reintroduce one.
  const [modal, modalContextHolder] = Modal.useModal();

  // A stack rather than a single slot: a slide-over can open a nested dialog
  // that registers its own guard, and the top-most registration is the one a
  // sidebar click belongs to. In practice only one of the three forms is open
  // at a time.
  const guardsRef = useRef([]);

  /**
   * Re-entrancy guard for `confirmLeaveFor`. A double-click on the panel's X
   * fires two `requestClose` calls before the first confirm has painted, and
   * `modal.confirm` happily stacks a second identical dialog — the user then
   * has to answer "Discard changes?" twice, and answering the top one leaves a
   * second dialog over an already-closed form.
   *
   * Keyed on the guard object rather than a single boolean, because the stack
   * allows a nested form to legitimately raise its own confirm. The second
   * caller gets back the *same* promise, so it resolves with whatever the user
   * actually chooses rather than being told "no" for a click that was never
   * refused. WeakMap: an entry keyed on a guard whose form unmounted with the
   * dialog still open would otherwise be held forever.
   */
  const pendingConfirmsRef = useRef(new WeakMap());

  const registerGuard = useCallback((guard) => {
    guardsRef.current = [...guardsRef.current, guard];
    return () => {
      guardsRef.current = guardsRef.current.filter((g) => g !== guard);
    };
  }, []);

  /** Synchronous: is any guard registered at all? The sidebar asks this first so
   *  that when no slide-over is open it can leave the click completely alone. */
  const hasGuard = useCallback(() => guardsRef.current.length > 0, []);

  /**
   * Resolves true when the caller may proceed.
   *
   * - no guard            -> true, nothing touched
   * - mid-save            -> false, with a notice. See the comment below.
   * - guard, but clean    -> closes the slide-over and resolves true, no prompt
   * - guard, and dirty    -> centered themed confirm; discard closes and
   *                          resolves true, cancel resolves false and leaves
   *                          the form exactly as it was
   */
  const confirmLeaveFor = useCallback((guard) => {
    if (!guard) return Promise.resolve(true);

    // Checked before anything else: once a confirm is on screen it owns the
    // decision, whatever `isDirty`/`isBusy` have since become.
    const inFlight = pendingConfirmsRef.current.get(guard);
    if (inFlight) return inFlight;

    // A submit is in flight. Prompting "Discard changes?" here would be a lie —
    // the request has already gone to the server and closing the form cannot
    // call it back. Blocking is the honest answer, and it is also the useful
    // one: these saves can fail (POFormModal.handleSaveError maps field errors
    // back onto the form), and the user has to still be looking at the form to
    // see and fix that. The alternative — queue the navigation and run it when
    // the save resolves — was rejected because it fires seconds later, after
    // the user has moved on, and it tears the form away exactly when a failure
    // needs their attention. This also matches what each form's own
    // `handleClose` already does: refuse to close while saving.
    if (guard.isBusy()) {
      toast.info(`${guard.label()} is still saving — one moment.`, {
        ...busyToastOpts,
        toastId: guard.busyToastId || 'unsaved-changes-busy',
      });
      return Promise.resolve(false);
    }

    if (!guard.isDirty()) {
      guard.discard();
      return Promise.resolve(true);
    }

    const pending = new Promise((resolve) => {
      // antd calls onCancel for the Cancel button, Esc and a mask click, but
      // guarding the resolve keeps this a one-shot promise regardless.
      let settled = false;
      const settle = (value) => {
        if (settled) return;
        settled = true;
        // Release the re-entrancy slot only if it is still ours; a form that
        // unmounted and re-registered could otherwise clear its successor's.
        if (pendingConfirmsRef.current.get(guard) === pending) {
          pendingConfirmsRef.current.delete(guard);
        }
        resolve(value);
      };
      modal.confirm({
        title: 'Discard changes?',
        content: `${guard.label()} has changes that haven't been saved. Leaving now discards them.`,
        okText: 'Discard changes',
        okButtonProps: { danger: true },
        cancelText: 'Keep editing',
        centered: true,
        onOk: () => {
          guard.discard();
          settle(true);
        },
        onCancel: () => settle(false),
        // Last resort, and the only one that covers a close which fires
        // neither callback — `Modal.destroyAll()`, or the dialog being torn
        // down from outside. Without it that path would leave the WeakMap
        // entry in place and every later close request for this guard would
        // return the same forever-pending promise, silently making the form
        // impossible to leave. `settle` is idempotent, so on the normal paths
        // this runs after onOk/onCancel and does nothing.
        afterClose: () => settle(false),
      });
    });

    pendingConfirmsRef.current.set(guard, pending);
    return pending;
  }, [modal]);

  const confirmLeave = useCallback(
    () => confirmLeaveFor(guardsRef.current[guardsRef.current.length - 1] || null),
    [confirmLeaveFor],
  );

  const value = useMemo(
    () => ({ registerGuard, hasGuard, confirmLeave, confirmLeaveFor }),
    [registerGuard, hasGuard, confirmLeave, confirmLeaveFor],
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {modalContextHolder}
      {children}
    </UnsavedChangesContext.Provider>
  );
};

export const useUnsavedChangesGate = () => useContext(UnsavedChangesContext) || NO_GUARD;

/**
 * Register an open form as guarded.
 *
 * `isDirty` and `isBusy` are read lazily, at the moment the user tries to
 * leave — the guard object holds a ref, not the values — so a form can
 * recompute them on every render without this hook re-registering or
 * re-rendering anything.
 *
 * @param {boolean}  active    the form is open; registration follows it
 * @param {boolean}  isDirty   whether leaving now would lose the user's work
 * @param {boolean}  isBusy    a submit is in flight; leaving is refused
 * @param {Function} onDiscard closes the form (pass the form's own `handleClose`)
 * @param {string}   label     how the form is named in the prompt
 * @returns {{ requestClose: Function }} wire to the modal's `onCancel` so the
 *          panel's X button and Esc go through the same prompt as the sidebar.
 */
export const useUnsavedChangesGuard = ({ active, isDirty, isBusy = false, onDiscard, label }) => {
  const { registerGuard, confirmLeaveFor } = useUnsavedChangesGate();

  const latest = useRef({ isDirty, isBusy, onDiscard, label });
  latest.current = { isDirty, isBusy, onDiscard, label };

  // Built once and kept stable, so `requestClose` can name *this* form's guard
  // rather than whatever is currently on top of the stack.
  const guardRef = useRef(null);
  if (!guardRef.current) {
    guardRef.current = {
      isDirty: () => !!latest.current.isDirty,
      isBusy: () => !!latest.current.isBusy,
      discard: () => latest.current.onDiscard?.(),
      label: () => latest.current.label || 'This form',
      // Stable for the life of this form, which is what makes repeated clicks
      // re-use one notice instead of stacking a queue of identical ones.
      busyToastId: nextBusyToastId(),
    };
  }

  // The "still saving" notice has no autoClose, so its lifetime is this effect:
  // raised by the provider while a submit is in flight, dismissed by the
  // cleanup the moment `isBusy` clears — or the form unmounts — whichever comes
  // first. A fixed 3s timeout used to expire mid-save on a slow request and
  // leave the user with no explanation of why the panel refused to close.
  useEffect(() => {
    if (!isBusy) return undefined;
    return () => toast.dismiss(guardRef.current.busyToastId);
  }, [isBusy]);

  useEffect(() => {
    if (!active) return undefined;
    return registerGuard(guardRef.current);
  }, [active, registerGuard]);

  // Partial cover for the gap left by `useBlocker` being unavailable on a
  // non-data router (see Sidebar.jsx): this catches tab close, reload and a
  // typed URL, though not in-app Back. Registered only while a guarded form is
  // actually dirty, so a clean form never triggers the browser's dialog, and
  // torn down by the same condition, so it cannot linger after close. Browsers
  // ignore any custom text and show their own.
  useEffect(() => {
    if (!active || !isDirty) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [active, isDirty]);

  const requestClose = useCallback(() => {
    confirmLeaveFor(guardRef.current);
  }, [confirmLeaveFor]);

  return { requestClose };
};

export default UnsavedChangesContext;
